import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, concatMap, from, of, toArray } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestStepService } from '../../../services/test-step.service';
import { AccountService } from '../../../account/account.service';
import { CreateTestStepRequest, TestStep, UpdateTestStepRequest } from '../../../models/test-case.model';
import { TestStepLibraryDialogComponent } from '../test-step-library-dialog/test-step-library-dialog.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import {
  DRAFT_ID,
  createDraftStep,
  createLocalModuleRefStep,
  createLocalStep,
  isDraftId,
  isLocalId,
  nextLocalId,
} from './test-step-local.util';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { ModuleEmbedDialogComponent } from '../../module/module-embed-dialog/module-embed-dialog.component';
import { ModuleStepTreeComponent } from '../../module/module-step-tree/module-step-tree.component';
import { TestModule } from '../../../models/test-module.model';
import { TestModuleStore } from '../../../services/test-module-store';
import { ScrollContainerDirective } from '../../../shared/scroll-container/scroll-container.directive';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import {
  TestdatenBlockComponent,
  TestdatenBlockRow,
  toTestdatenblattSummary,
} from '../../../components/testdaten-block/testdaten-block.component';
import { StepConditionEditorComponent } from '../../../components/step-condition-editor/step-condition-editor.component';
import { TdbOverride, bedingungFragment, formatTcdlPreview, tdbIdSegment } from '../../../shared/tcdl-ops';
import { tdbRowSelectionLabel } from '../../../shared/tdb-row-label';
import { TdbOverrideDelta } from '../../../shared/tdb-override-delta';
import {
  formatFilterExpression,
  fromStepCondition,
  missingConditionFields,
  nextStepCondition,
  parseFilterExpression,
} from '../../../shared/data-sheet-filters.codec';
import { TcdlEditorComponent } from '../../../components/tcdl-editor/tcdl-editor.component';
import { StepKeyPillComponent } from '../../../components/step-key-pill/step-key-pill.component';
import { KiMarkerComponent } from '../../../components/ki/ki-marker/ki-marker.component';

/** Read-Mode kollabiert TCDL, sobald mehr als so viele Zeilen vorhanden sind (NEX-1047). */
const TCDL_COLLAPSE_THRESHOLD = 4;

export interface FlushResult {
  persisted: TestStep[];
  failed: TestStep[];
}

type FlushItem = TestStep | { failed: TestStep };

function isPersisted(item: FlushItem): item is TestStep {
  return !('failed' in item);
}

function isFailed(item: FlushItem): item is { failed: TestStep } {
  return 'failed' in item;
}

@Component({
  selector: 'app-test-step-table',
  imports: [
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    TestStepLibraryDialogComponent,
    ModuleEmbedDialogComponent,
    ModuleStepTreeComponent,
    SectionTitleComponent,
    AvatarComponent,
    ScrollContainerDirective,
    TestdatenBlockComponent,
    StepConditionEditorComponent,
    TcdlEditorComponent,
    StepKeyPillComponent,
    KiMarkerComponent,
  ],
  templateUrl: './test-step-table.component.html',
  styleUrl: './test-step-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepTableComponent implements OnInit {
  private readonly stepService = inject(TestStepService);
  private readonly accountService = inject(AccountService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleStore = inject(TestModuleStore);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);
  /** Modul-Scope-Owner (NEX-593): schließt {@link testCaseId} exklusiv aus. */
  readonly moduleId = input<string | null>(null);
  /**
   * NEX-538/NEX-644: Erlaubt das Einbinden ganzer Module als MODULE_REF-Zeile. Testfall-
   * UND Modul-Editor setzen das auf true (der Modul-Editor seit NEX-644, das separate
   * TestModuleEmbed-Konzept ist abgelöst) — unabhängig davon, ob der Owner schon
   * persistiert ist (Anlegen + Bearbeiten).
   */
  readonly allowModuleEmbed = input(false);

  /**
   * Buffered-Mode für den Testfall-Bulk-Save (NEX-599): unterdrückt alle
   * Owner-Service-Calls (create/update/delete/reorder/copyFromLibrary) und
   * hält alle Mutationen lokal — der Parent puffert und persistiert später
   * per {@code saveFull}. {@link initialSteps} muss dann die Startliste liefern.
   */
  readonly bufferedMode = input(false);
  readonly initialSteps = input<TestStep[] | null>(null);

  /** NEX-607: unterdrückt jede Mutation für PROJECT_READER, unabhängig vom Owner-Formular. */
  readonly readOnly = input(false);

  /**
   * NEX-1051: projektweite Testdatenblatt-Liste — der Parent laedt sie einmal und
   * reicht sie durch. Wird nur fuer die Chip-/Block-Ansicht am MODULE_REF-Row gebraucht.
   */
  readonly availableSheets = input<readonly Testdatenblatt[]>([]);

  /**
   * NEX-1296: zeigt an gespeicherten Schritten die Nummer (`TS-035`) als kopierbare Pille.
   * Nur der Testfall-Editor schaltet das ein — dieselbe Tabelle steckt im Modul-Editor.
   */
  readonly showStepKeys = input(false);

  readonly kiBefundIds = input<readonly string[]>([]);
  /** Schritt des zuletzt angeklickten Befunds; sein Marker wird kräftig dargestellt. */
  readonly kiHervorgehobenId = input<string | null>(null);
  protected readonly kiBefundSet = computed(() => new Set(this.kiBefundIds()));
  protected readonly kiGeaendertIds = signal<ReadonlySet<string>>(new Set());

  /** Emitted on every user step mutation so the parent form can mark itself dirty (NEX-535). */
  readonly changed = output<void>();

  /** Snapshot der aktuellen Steps nach jeder User-Mutation (NEX-599 Buffered-Mode). */
  readonly stepsChanged = output<TestStep[]>();

  /**
   * NEX-644: Sprung zum eingebundenen Modul (calledModuleId einer MODULE_REF-Zeile).
   * Der Parent navigiert selbst, damit seine Dirty-Behandlung greift — bei Modul→Modul
   * feuert der CanDeactivate-Guard nicht (gleiche Route, nur Param-Wechsel).
   */
  readonly moduleOpen = output<string>();

  /**
   * NEX-686/NEX-1052: Bearbeiten eines eingebundenen Moduls im Right-Drawer (ohne die
   * Seite zu verlassen). Zusaetzlich zur `moduleId` wird die `stepId` der ausloesenden
   * MODULE_REF-Zeile mitgegeben (null bei geschachtelten Modulen aus dem Baum) — der
   * Testfall-Editor braucht sie, um das TDB-Override im Drawer an genau diesen Aufruf
   * zu binden.
   */
  readonly editModule = output<{ moduleId: string; stepId: string | null }>();

  readonly hasTestCase = computed(() => !!this.testCaseId());
  readonly hasOwner = computed(() => !!this.testCaseId() || !!this.moduleId());
  /** Lokal-Modus: entweder explizit gebuffert (NEX-599) oder Anlegen ohne Owner (NEX-593). */
  private readonly isBuffered = computed(() => this.bufferedMode() || !this.hasOwner());

  readonly steps = signal<TestStep[]>([]);
  readonly draftStep = signal<TestStep | null>(null);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly busy = signal(false);

  readonly currentUserName = signal('');

  readonly tableRows = computed<TestStep[]>(() => {
    const draft = this.draftStep();
    return draft ? [...this.steps(), draft] : this.steps();
  });

  readonly passedCount = computed(() => 0);

  readonly expandedTcdlRows = signal<ReadonlySet<string>>(new Set<string>());
  /**
   * Globaler Toggle (NEX-1047) — macht TCDL-Bloecke aller Zeilen sichtbar. Der
   * Zustand wird pro Owner (Testfall bzw. Modul) in `localStorage` gemerkt,
   * damit er beim erneuten Oeffnen wieder eingeblendet ist; bei fehlender Web-
   * Storage-Umgebung (SSR, Test-Sandbox) bleibt es rein reaktiv.
   */
  readonly tcdlAll = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly editTitle = signal('');
  readonly editAction = signal('');
  readonly editExpected = signal('');
  readonly editNotes = signal('');
  readonly editTcdl = signal('');
  private editTcdlOriginal = '';

  /**
   * Pflichtfelder wie in der Standalone-Anlage (TestStepFormComponent: title + expectedResult
   * required); zusätzlich sperrt ein Titel über FIELD_LENGTHS.NAME_MAX Zeichen.
   */
  readonly canSaveEdit = computed(() => {
    const title = this.editTitle().trim();
    return !!title && title.length <= FIELD_LENGTHS.NAME_MAX && !!this.editExpected().trim();
  });

  readonly fieldLengths = FIELD_LENGTHS;

  private localIdCounter = 0;

  readonly showLibrary = signal(false);

  /** NEX-538: Modul-Einbinden-Dialog — nur im Testfall-Kontext ({@link hasTestCase}). */
  readonly showModuleEmbed = signal(false);

  /** IDs der bereits als MODULE_REF-Zeile eingebundenen Module → dedupe. */
  readonly embeddedModuleIds = computed(() =>
    this.steps()
      .filter((s) => s.kind === 'MODULE_REF')
      .map((s) => s.calledModuleId)
      .filter((id): id is string => !!id),
  );

  /**
   * Auswahl im Modul-Einbinden-Dialog: bereits eingebundene Module plus — im Modul-Editor
   * (NEX-644) — das Owner-Modul selbst, damit sich ein Modul nicht selbst einbinden kann.
   */
  readonly moduleEmbedExcludeIds = computed(() => {
    const ownerModuleId = this.moduleId();
    return ownerModuleId ? [ownerModuleId, ...this.embeddedModuleIds()] : this.embeddedModuleIds();
  });

  /** Aufgeklappte MODULE_REF-Zeilen (Step-ID). */
  readonly expandedModuleRows = signal<ReadonlySet<string>>(new Set());
  /** Lazy geladene Modul-Schritte je MODULE_REF-Zeile (Step-ID → Schritte). */
  private readonly moduleStepsCache = signal<Record<string, TestStep[]>>({});
  /** Bekannte Schrittanzahl je MODULE_REF-Zeile (aus Embed oder Lazy-Load); fehlt = noch unbekannt. */
  private readonly moduleStepCounts = signal<Record<string, number>>({});
  /** MODULE_REF-Zeilen, deren Lazy-Load fehlgeschlagen ist (Netz/Rechte) — abgrenzbar von "leer". */
  private readonly moduleLoadErrors = signal<ReadonlySet<string>>(new Set());

  readonly librarySteps = signal<TestStep[]>([]);

  /** Bibliotheks-IDs, von denen eine Kopie am Testfall/Modul hängt (NEX-462 Backref). */
  private readonly activeLibraryStepIds = computed(
    () =>
      new Set(
        this.steps()
          .map((s) => s.libraryStepId)
          .filter((id): id is string => !!id),
      ),
  );

  private readonly editTitleInput = viewChild<ElementRef<HTMLInputElement>>('editTitleInput');
  private readonly editNotesInput = viewChild<ElementRef<HTMLInputElement>>('editNotesInput');
  private readonly editTcdlEditor = viewChild(TcdlEditorComponent);
  private focusTcdlOnEdit = false;

  onTcdlAdvance(): void {
    this.editNotesInput()?.nativeElement.focus();
  }

  constructor() {
    effect(() => {
      if (this.editingId() !== null) {
        queueMicrotask(() => this.focusEditRow());
      }
    });
  }

  private focusEditRow(): void {
    const editor = this.focusTcdlOnEdit ? this.editTcdlEditor() : undefined;
    this.focusTcdlOnEdit = false;
    if (editor) {
      editor.focusTextarea();
    } else {
      this.editTitleInput()?.nativeElement.focus();
    }
  }

  ngOnInit(): void {
    this.restoreTcdlAll();
    if (this.bufferedMode()) {
      this.steps.set(this.initialSteps() ?? []);
      this.loading.set(false);
    } else {
      const listCall = this.ownerListCall();
      if (listCall) {
        listCall.subscribe({
          next: (steps) => {
            this.steps.set(steps);
            this.loading.set(false);
          },
          error: () => {
            this.error.set(true);
            this.loading.set(false);
          },
        });
      } else {
        this.loading.set(false);
      }
    }
    // Bibliothek unabhaengig vom Owner laden: der Bibliotheks-Dialog + die Vorschlaege
    // funktionieren auch im Anlegen-Modus (Owner existiert noch nicht), NEX-593.
    this.loadLibrary();
    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => this.currentUserName.set(profile.displayName),
        error: () => this.currentUserName.set(''),
      });
  }

  onEditTitleInput(event: Event): void {
    this.editTitle.set(this.inputValue(event));
  }

  onEditActionInput(event: Event): void {
    this.editAction.set(this.inputValue(event));
  }

  onEditExpectedInput(event: Event): void {
    this.editExpected.set(this.inputValue(event));
  }

  onEditNotesInput(event: Event): void {
    this.editNotes.set(this.inputValue(event));
  }

  startDraft(): void {
    if (this.readOnly() || this.busy() || this.draftStep()) {
      return;
    }
    this.draftStep.set(createDraftStep(this.steps().length));
    this.resetEditState();
    this.editingId.set(DRAFT_ID);
  }

  isDraft(step: TestStep): boolean {
    return isDraftId(step.id);
  }

  openLibrary(): void {
    if (!this.readOnly() && !this.busy()) {
      this.showLibrary.set(true);
    }
  }

  openModuleEmbed(): void {
    if (!this.readOnly() && !this.busy()) {
      this.showModuleEmbed.set(true);
    }
  }

  /** NEX-538: hängt pro gewähltem Modul eine lokale MODULE_REF-Zeile an (dedupe via {@link embeddedModuleIds}). */
  onModuleEmbedApplied(modules: TestModule[]): void {
    if (this.readOnly() || this.busy() || modules.length === 0) {
      return;
    }
    const already = new Set(this.embeddedModuleIds());
    const newRows: TestStep[] = [];
    const counts: Record<string, number> = {};
    for (const module of modules) {
      if (already.has(module.id)) continue;
      already.add(module.id);
      const id = nextLocalId(++this.localIdCounter);
      newRows.push(createLocalModuleRefStep(id, this.steps().length + newRows.length, module.id, module.name));
      if (module.stepCount != null) counts[id] = module.stepCount;
    }
    if (newRows.length === 0) return;
    this.moduleStepCounts.update((c) => ({ ...c, ...counts }));
    this.steps.update((list) => [...list, ...newRows]);
    this.emitChange();
  }

  isModuleRow(step: TestStep): boolean {
    return step.kind === 'MODULE_REF';
  }

  /**
   * Durchgängige hierarchische Nummer eines Kindes einer obersten Modul-Zeile (NEX-826):
   * die Modul-Zeile selbst steht an Position `moduleRowIndex + 1`, ihre Kinder (Schritte UND
   * Untermodule, gemischt) an `{P}.{childIndex + 1}`. Als String, damit `3.1` nicht als Float
   * geparst wird.
   */
  moduleStepNumber(moduleRowIndex: number, childIndex: number): string {
    return `${moduleRowIndex + 1}.${childIndex + 1}`;
  }

  /** NEX-644: Sprung zum eingebundenen Modul — der Parent navigiert (siehe {@link moduleOpen}). */
  openModule(step: TestStep): void {
    if (step.calledModuleId) {
      this.moduleOpen.emit(step.calledModuleId);
    }
  }

  /** NEX-686: Bearbeiten des eingebundenen Moduls im Right-Drawer (siehe {@link editModule}). */
  onEditModule(step: TestStep): void {
    if (!this.readOnly() && step.calledModuleId) {
      this.editModule.emit({ moduleId: step.calledModuleId, stepId: step.id });
    }
  }

  /** NEX-686: Edit-Wunsch aus einem (verschachtelten) Modul-Baum an den Parent weiterreichen. */
  onNestedModuleEdit(moduleId: string): void {
    if (!this.readOnly()) {
      this.editModule.emit({ moduleId, stepId: null });
    }
  }

  isModuleExpanded(stepId: string): boolean {
    return this.expandedModuleRows().has(stepId);
  }

  hasTcdl(step: TestStep): boolean {
    return Boolean(step.tcdl?.trim());
  }

  isTcdlExpanded(stepId: string): boolean {
    return this.expandedTcdlRows().has(stepId);
  }

  toggleTcdl(stepId: string): void {
    this.expandedTcdlRows.update((current) => {
      const next = new Set(current);
      if (!next.delete(stepId)) {
        next.add(stepId);
      }
      return next;
    });
  }

  toggleTcdlAll(): void {
    this.tcdlAll.update((v) => !v);
    this.persistTcdlAll(this.tcdlAll());
  }

  /**
   * Projektweite Persistenz (NEX-1047 Review): wer TCDL sehen will, will es
   * meist ueberall im Projekt sehen. Vermeidet den Wildwuchs pro Testfall und
   * greift auch fuer neu angelegte Owner ohne eigene ID.
   */
  private tcdlAllStorageKey(): string {
    return `to.tcdlAll:${this.projectId()}`;
  }

  private restoreTcdlAll(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(this.tcdlAllStorageKey());
      if (raw === 'true') this.tcdlAll.set(true);
      else if (raw === 'false') this.tcdlAll.set(false);
    } catch {
      /* Storage abgelehnt (Private-Mode, Quota) — im Zweifel Session-only. */
    }
  }

  private persistTcdlAll(value: boolean): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.tcdlAllStorageKey(), String(value));
    } catch {
      /* Storage abgelehnt — Zustand bleibt im Signal, wird nur nicht gemerkt. */
    }
  }

  /** Passt die Subline dem Owner-Kontext an (Testfall vs. Testmodul). */
  readonly tcdlToggleAllSublineKey = computed<string>(() =>
    this.moduleId() ? 'testSteps.form.tcdlToggleAllSublineModule' : 'testSteps.form.tcdlToggleAllSubline',
  );

  /** Rohzeilen für die Read-Mode-Ansicht (Zeilennummer + M-Marker im Design). */
  tcdlRawLines(step: TestStep): string[] {
    return (step.tcdl ?? '').split('\n');
  }

  tcdlInstructionCount(step: TestStep): number {
    return this.tcdlRawLines(step).filter((l) => l.trim().length > 0).length;
  }

  tcdlManualCount(step: TestStep): number {
    const lines = this.tcdlRawLines(step);
    return lines.filter((l, i) => !l.trim() && i < lines.length - 1).length;
  }

  tcdlIsLong(step: TestStep): boolean {
    return this.tcdlRawLines(step).length > TCDL_COLLAPSE_THRESHOLD;
  }

  tcdlVisibleLines(step: TestStep): string[] {
    const all = this.tcdlRawLines(step);
    if (!this.tcdlIsLong(step) || this.isTcdlExpanded(step.id)) return all;
    return all.slice(0, TCDL_COLLAPSE_THRESHOLD);
  }

  tcdlHiddenLineCount(step: TestStep): number {
    return Math.max(0, this.tcdlRawLines(step).length - TCDL_COLLAPSE_THRESHOLD);
  }

  onEditTcdlChange(value: string): void {
    this.editTcdl.set(value);
  }

  moduleSteps(stepId: string): TestStep[] {
    return this.moduleStepsCache()[stepId] ?? [];
  }

  /** Bekannte Schrittanzahl der Modul-Zeile, sonst null (Badge wird dann ausgeblendet). */
  moduleStepCount(stepId: string): number | null {
    return this.moduleStepCounts()[stepId] ?? null;
  }

  /**
   * NEX-691: Modul-Titel der Zeile, live aus dem {@link TestModuleStore} gespiegelt, sobald
   * das Modul im Drawer gespeichert wurde; sonst der Snapshot-Titel der eingebundenen Zeile.
   */
  liveModuleTitle(step: TestStep): string {
    const live = step.calledModuleId ? this.moduleStore.watch(step.calledModuleId)() : null;
    return live?.name ?? step.title ?? '—';
  }

  /** NEX-691: Schritt-Count der Modul-Zeile, live aus dem Store bevorzugt, sonst der Lazy-Load-Cache. */
  liveModuleStepCount(step: TestStep): number | null {
    const live = this.liveModule(step);
    if (live) {
      if (live.stepCount != null) {
        return live.stepCount;
      }
      if (live.steps) {
        return live.steps.length;
      }
    }
    return this.moduleStepCount(step.id);
  }

  /**
   * NEX-691: Aufgeklappte Modul-Schritte — nach einem Drawer-Save aus dem Live-Store
   * (frischer Stand inkl. verschachtelter Module), sonst der Lazy-Load-Cache. Sorgt dafür,
   * dass Änderungen am Modul sofort in der Testschritt-Tabelle sichtbar sind.
   */
  liveModuleSteps(step: TestStep): TestStep[] {
    const live = this.liveModule(step);
    if (live?.steps) {
      return [...live.steps].sort((a, b) => a.orderIndex - b.orderIndex);
    }
    return this.moduleSteps(step.id);
  }

  /** Store-Detail des eingebundenen Moduls, falls bereits geladen/gespeichert (sonst null). */
  private liveModule(step: TestStep): (TestModule & { steps?: TestStep[] }) | null {
    if (!step.calledModuleId) {
      return null;
    }
    return this.moduleStore.watch(step.calledModuleId)() as (TestModule & { steps?: TestStep[] }) | null;
  }

  /** True, wenn der letzte Lazy-Load dieser Modul-Zeile fehlgeschlagen ist (nicht: leeres Modul). */
  moduleLoadFailed(stepId: string): boolean {
    return this.moduleLoadErrors().has(stepId);
  }

  /** Auf-/Zuklappen einer MODULE_REF-Zeile; lädt die Modul-Schritte beim ersten Aufklappen lazy. */
  toggleModuleExpand(step: TestStep): void {
    const willExpand = !this.isModuleExpanded(step.id);
    this.expandedModuleRows.update((current) => {
      const next = new Set(current);
      if (next.has(step.id)) {
        next.delete(step.id);
      } else {
        next.add(step.id);
      }
      return next;
    });
    if (willExpand) this.loadModuleSteps(step);
  }

  /** Erneuter Ladeversuch nach einem fehlgeschlagenen Lazy-Load. */
  retryModuleLoad(step: TestStep): void {
    this.loadModuleSteps(step);
  }

  /** Lädt die Modul-Schritte einmalig; ein Fehler wird markiert (getrennt von "leer") und ist retry-bar. */
  private loadModuleSteps(step: TestStep): void {
    if (!step.calledModuleId || this.moduleStepsCache()[step.id] !== undefined) return;
    this.moduleLoadErrors.update((errors) => {
      const next = new Set(errors);
      next.delete(step.id);
      return next;
    });
    this.stepService
      .listForModule(this.projectId(), step.calledModuleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.moduleStepsCache.update((cache) => ({ ...cache, [step.id]: steps }));
          this.moduleStepCounts.update((c) => ({ ...c, [step.id]: steps.length }));
        },
        error: () =>
          this.moduleLoadErrors.update((errors) => {
            const next = new Set(errors);
            next.add(step.id);
            return next;
          }),
      });
  }

  // ── NEX-1051: TDB-Chips + Override-Block am MODULE_REF-Row ───────────────

  /**
   * Zwischen-Zustand pro Step: die vier Override-Felder aus dem Block. Wird
   * gebraucht, weil einzelne Patches (nur `subj`, nur `ref`) fuer sich keine
   * gueltige VALUE_COMPARE-Bedingung ergeben — das BE akzeptiert nur vollstaendige.
   * So verlieren Zwischenschritte des Nutzers keine Eingaben.
   */
  private readonly overrideDrafts = signal<Record<string, TdbOverride>>({});

  /** Positionen wie die Nummernblase der Zeile, damit der Hinweis am Speichern-Knopf dieselbe Zahl nennt. */
  readonly incompleteConditionStepNumbers = computed<number[]>(() =>
    this.tableRows().flatMap((step, index) =>
      this.isModuleRow(step) && missingConditionFields(this.currentOverride(step)).length > 0 ? [index + 1] : [],
    ),
  );

  /** Damit die Modul-Seitenleiste mit der halben Eingabe der Zeile startet statt mit der gespeicherten Bedingung. */
  overrideDraftFor(stepId: string): TdbOverride | null {
    return this.overrideDrafts()[stepId] ?? null;
  }

  protected currentOverride(step: TestStep): TdbOverride {
    const draft = this.overrideDrafts()[step.id];
    if (draft) return draft;
    return {
      ...fromStepCondition(step.stepCondition ?? null),
      filter: formatFilterExpression(step.dataSheetFilters),
    };
  }

  /** Alle mit dem eingebundenen Modul verknuepften Testdatenblaetter. */
  linkedSheetsForStep(step: TestStep): Testdatenblatt[] {
    const moduleId = step.calledModuleId;
    if (!moduleId) return [];
    return this.availableSheets().filter((s) => s.testModuleIds?.includes(moduleId));
  }

  /**
   * BE-Limit (NEX-1004): pro MODULE_REF-Schritt kann nur EIN Datenblatt einen
   * Override tragen. Chips fuer die restlichen TDBs zeigen die volle Zeilenzahl,
   * der aktive Chip zeigt „eingeschraenkt".
   */
  isChipRestricted(step: TestStep, sheet: Testdatenblatt): boolean {
    if (step.dataSheetId !== sheet.id) return false;
    return this.hasActiveOverride(step);
  }

  chipLabel(step: TestStep, sheet: Testdatenblatt): string {
    const total = sheet.zeilen.length;
    const restricted = this.isChipRestricted(step, sheet);
    const filter = restricted ? formatFilterExpression(step.dataSheetFilters) : '';
    // Ein Override nur aus einer Bedingung (keine Zeilen-Auswahl) laeuft ueber alle
    // Zeilen, ist aber eingeschraenkt — „4 von 4 Zeilen" haelt das auseinander,
    // waehrend „4 Zeilen" wortgleich mit einem Schritt ohne Override waere.
    const rows =
      restricted && !filter
        ? this.translate.instant('tdb.status.rowsExact', { count: total, total })
        : tdbRowSelectionLabel(this.translate, total, filter);
    return this.translate.instant('tdb.chip.label', { key: sheet.key, rows });
  }

  chipTooltip(step: TestStep, sheet: Testdatenblatt): string {
    if (this.isChipRestricted(step, sheet)) {
      const filter = formatFilterExpression(step.dataSheetFilters);
      return this.translate.instant('tdb.chip.tooltipRestricted', {
        key: sheet.key,
        name: sheet.name,
        selection: filter || this.translate.instant('tdb.chip.conditionOnly'),
      });
    }
    return this.translate.instant('tdb.chip.tooltipAll', {
      key: sheet.key,
      name: sheet.name,
      count: sheet.zeilen.length,
    });
  }

  blockRows(step: TestStep): TestdatenBlockRow[] {
    const sheet = this.linkedSheetsForStep(step).find((s) => s.id === step.dataSheetId);
    if (!sheet) return [];
    return [{ tdb: toTestdatenblattSummary(sheet), override: this.currentOverride(step) }];
  }

  moduleTitleFor(step: TestStep): string {
    return this.liveModuleTitle(step);
  }

  /**
   * Der Block sendet Aenderungen als UUID des Datenblatts nicht kennt — er kennt nur
   * den TDB-Key. Wir mappen zurueck auf die interne UUID via {@code activeSheet}.
   */
  onBlockOverrideChanged(step: TestStep, event: { tdbId: string; patch: Partial<TdbOverride> }): void {
    if (this.readOnly()) return;
    const sheet = this.linkedSheetsForStep(step).find((s) => s.key === event.tdbId);
    if (!sheet) return;
    const nextOverride = this.draftOverride(step, event.patch);
    this.mutateStep(step.id, (s) => ({
      ...s,
      dataSheetId: sheet.id,
      dataSheetFilters: parseFilterExpression(nextOverride.filter ?? '').entries,
      stepCondition: nextStepCondition(s.stepCondition, event.patch, nextOverride),
    }));
  }

  /** NEX-1084: Bedingung eines Modulaufrufs ohne Datenblatt — Datenblatt und Filter bleiben. */
  onConditionChanged(step: TestStep, patch: Partial<TdbOverride>): void {
    if (this.readOnly()) return;
    const nextOverride = this.draftOverride(step, patch);
    this.mutateStep(step.id, (s) => ({ ...s, stepCondition: nextStepCondition(s.stepCondition, patch, nextOverride) }));
  }

  moduleTcdlPreview(step: TestStep): string {
    const override = this.currentOverride(step);
    const sheetRow = this.blockRows(step)[0];
    const idSegment = sheetRow ? tdbIdSegment(sheetRow.tdb.id, override.filter) : '';
    return formatTcdlPreview(this.moduleTitleFor(step), idSegment, bedingungFragment(override));
  }

  private draftOverride(step: TestStep, patch: Partial<TdbOverride>): TdbOverride {
    const nextOverride: TdbOverride = { ...this.currentOverride(step), ...patch };
    this.overrideDrafts.update((drafts) => ({ ...drafts, [step.id]: nextOverride }));
    return nextOverride;
  }

  onBlockOverrideReset(step: TestStep): void {
    if (this.readOnly()) return;
    this.discardOverrideDraft(step.id);
    this.mutateStep(step.id, (s) => ({
      ...s,
      dataSheetId: null,
      dataSheetFilters: null,
      stepCondition: null,
    }));
  }

  private discardOverrideDraft(stepId: string): void {
    this.overrideDrafts.update((drafts) => {
      const next = { ...drafts };
      delete next[stepId];
      return next;
    });
  }

  private hasActiveOverride(step: TestStep): boolean {
    const hasFilter = (step.dataSheetFilters?.length ?? 0) > 0;
    const hasCondition = !!step.stepCondition && Object.keys(step.stepCondition).length > 0;
    return hasFilter || hasCondition;
  }

  /**
   * NEX-1052 Follow-up: Extern getriebene Aktualisierung eines MODULE_REF-Overrides,
   * z. B. wenn der Modul-Drawer eine TDB-Zuordnung im Testfall-Kontext emittiert.
   * Ohne diesen Weg wuerde die interne `steps`-Signalliste nach dem einmaligen
   * `initialSteps`-Seed im `ngOnInit` nicht mehr synchronisiert.
   */
  patchStepOverride(stepId: string, delta: Omit<TdbOverrideDelta, 'stepId'>): void {
    // Die Übernahme aus dem Drawer ist die spätere Eingabe: Sie ersetzt den Zeilen-Entwurf, eine halbe bleibt als Entwurf stehen.
    const override = delta.override;
    if (override) {
      this.overrideDrafts.update((drafts) => ({ ...drafts, [stepId]: override }));
    } else {
      this.discardOverrideDraft(stepId);
    }
    this.mutateStep(stepId, (s) => ({
      ...s,
      dataSheetId: delta.dataSheetId,
      dataSheetFilters: delta.dataSheetFilters,
      stepCondition: delta.stepCondition,
    }));
  }

  /**
   * Übernimmt einen Vorschlag des KI-Qualitätschecks als ungespeicherte Änderung. Der
   * Schritt-Text ist der Titel, bei Altbestand ohne Titel die Aktion — wie er an die KI ging.
   */
  uebernehmeKiVorschlag(stepId: string, feld: 'ERWARTUNG' | 'SCHRITT', wert: string): void {
    if (this.readOnly()) return;
    const schritt = this.steps().find((s) => s.id === stepId);
    if (!schritt) return;
    const mitTitel = !!schritt.title?.trim();
    const titel = wert.slice(0, FIELD_LENGTHS.NAME_MAX);
    this.mutateStep(stepId, (s) => {
      if (feld === 'ERWARTUNG') return { ...s, expectedResult: wert };
      return mitTitel ? { ...s, title: titel } : { ...s, action: wert };
    });
    this.kiGeaendertIds.update((ids) => new Set(ids).add(stepId));
    // Ein offener Inline-Editor hält eigene Werte; ohne Nachziehen überschriebe „Übernehmen“ den Vorschlag still.
    if (this.editingId() !== stepId) return;
    if (feld === 'ERWARTUNG') {
      this.editExpected.set(wert);
      return;
    }
    // Ohne Titel füllt startEdit das Titelfeld mit der Aktion; „Übernehmen“ schriebe sonst die alte Aktion als Titel.
    this.editTitle.set(titel);
    if (!mitTitel) this.editAction.set(wert);
  }

  zeigeSchritt(stepId: string): void {
    const zeile = this.host.nativeElement.querySelector<HTMLElement>(`[data-step-id="${CSS.escape(stepId)}"]`);
    zeile?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private mutateStep(stepId: string, transform: (s: TestStep) => TestStep): void {
    let found = false;
    this.steps.update((list) =>
      list.map((s) => {
        if (s.id !== stepId) return s;
        found = true;
        return transform(s);
      }),
    );
    if (found) {
      this.emitChange();
    }
  }

  onLibraryApplied(libraryStepIds: string[]): void {
    if (this.readOnly() || this.busy() || libraryStepIds.length === 0) {
      return;
    }
    if (this.isBuffered()) {
      // Anlegen-Modus (NEX-593) oder Buffered-Mode (NEX-599): die Auswahl lokal sammeln.
      this.appendLocalLibraryCopies(libraryStepIds);
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    from(libraryStepIds)
      .pipe(
        concatMap((id) => this.ownerCopyFromLibraryCall(id)!),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        // Jeden kopierten Schritt einzeln anhaengen: bricht ein spaeterer Aufruf ab
        // (z. B. 409/Netzfehler), bleiben die bereits persistierten Schritte sichtbar,
        // statt erst nach einem Reload aufzutauchen (Review NEX-537).
        next: (created) => {
          this.steps.update((list) => [...list, created]);
          this.emitChange();
        },
        error: () => this.failMutation(),
        complete: () => this.busy.set(false),
      });
  }

  /**
   * NEX-546: Baut fuer die eingebundenen Bibliotheks-Schritte Referenz-Zeilen (kein Inhalts-Copy).
   * Jede Zeile traegt libraryStepId = Bibliotheks-ID; der Mapper macht daraus testStepId
   * (Verknuepfung). Inhalt wird nur zur Anzeige aus der Definition uebernommen. Bereits in DIESEM
   * Kontext verknuepfte Definitionen werden uebersprungen (keine Dublette je Verwendung).
   */
  private appendLocalLibraryCopies(libraryStepIds: string[]): void {
    const lib = new Map(this.librarySteps().map((s) => [s.id, s]));
    const alreadyLinked = this.activeLibraryStepIds();
    const newLocals: TestStep[] = [];
    for (const id of libraryStepIds) {
      const src = lib.get(id);
      if (!src || alreadyLinked.has(id)) continue;
      newLocals.push(
        createLocalStep(
          nextLocalId(++this.localIdCounter),
          this.steps().length + newLocals.length,
          src.action ?? '',
          src.expectedResult ?? null,
          src.notes ?? null,
          // NEX-546: verlustfreier Round-Trip — ALLE Inhaltsfelder der Definition uebernehmen,
          // sonst nullt der Link-Save sie auf der geteilten Definition (Marc, #365).
          {
            title: src.title ?? null,
            libraryStepId: src.id,
            kind: src.kind,
            status: src.status,
            calledModuleId: src.calledModuleId ?? null,
            stepRole: src.stepRole,
            category: src.category ?? null,
            assigneeUserId: src.assigneeUserId ?? null,
            tags: src.tags ?? [],
          },
        ),
      );
    }
    if (newLocals.length === 0) return;
    this.steps.update((list) => [...list, ...newLocals]);
    this.emitChange();
  }

  private loadLibrary(): void {
    this.stepService
      .listLibrary(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => this.librarySteps.set(steps),
        error: () => this.librarySteps.set([]),
      });
  }

  startEdit(step: TestStep): void {
    if (this.readOnly()) {
      return;
    }
    this.editingId.set(step.id);
    // Titel und Aktion sind seit NEX-615 getrennte Felder (analog Standalone-Form).
    // Altbestand ohne Titel faellt auf die Aktion zurueck, damit das Pflicht-Titelfeld
    // beim ersten Bearbeiten nicht leer bleibt. Der Vorschlag wird gekuerzt, weil das
    // maxlength des Feldes nur Eingaben begrenzt, nicht Werte, die der Code setzt.
    this.editTitle.set(step.title ?? (step.action ?? '').slice(0, FIELD_LENGTHS.NAME_MAX));
    this.editAction.set(step.action ?? '');
    this.editExpected.set(step.expectedResult ?? '');
    this.editNotes.set(step.notes ?? '');
    this.editTcdl.set(step.tcdl ?? '');
    this.editTcdlOriginal = step.tcdl ?? '';
  }

  startEditTcdl(step: TestStep): void {
    if (this.readOnly()) {
      return;
    }
    this.focusTcdlOnEdit = true;
    this.startEdit(step);
  }

  cancelEdit(): void {
    this.draftStep.set(null);
    this.resetEditState();
  }

  saveEdit(): void {
    const id = this.editingId();
    if (this.readOnly() || !id || !this.canSaveEdit() || this.busy()) {
      return;
    }
    const title = this.editTitle().trim();
    const action = this.editAction().trim();
    if (isDraftId(id)) {
      this.persistDraft(title, action);
      return;
    }
    if (isLocalId(id) || this.isBuffered()) {
      this.updateLocalStep(id, title, action);
      return;
    }
    this.persistUpdate(id, title, action);
  }

  private persistUpdate(id: string, title: string, action: string): void {
    // Titel und Aktion werden seit NEX-615 getrennt gepatcht; frueher trug bei
    // Bibliotheks-Kopien die Aktion faelschlich den Titel.
    const request: UpdateTestStepRequest = {
      title,
      action,
      expectedResult: this.editExpected().trim() || null,
      notes: this.editNotes().trim() || null,
      ...(this.editTcdl() === this.editTcdlOriginal ? {} : { tcdl: this.editTcdl().trim() ? this.editTcdl() : '' }),
    };
    const call = this.ownerUpdateCall(id, request);
    if (!call) {
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: (updated) => {
        this.steps.update((list) => list.map((s) => (s.id === updated.id ? updated : s)));
        this.cancelEdit();
        this.busy.set(false);
        this.emitChange();
      },
      error: () => this.failMutation(),
    });
  }

  delete(step: TestStep): void {
    if (this.readOnly()) {
      return;
    }
    if (!confirm(this.translate.instant('testCases.form.testSteps.deleteConfirm'))) {
      return;
    }
    this.removeStep(step);
  }

  moveUp(index: number): void {
    if (this.readOnly()) {
      return;
    }
    if (index > 0) {
      this.swap(index, index - 1);
    }
  }

  moveDown(index: number): void {
    if (this.readOnly()) {
      return;
    }
    if (index < this.steps().length - 1) {
      this.swap(index, index + 1);
    }
  }

  private persistDraft(title: string, action: string): void {
    const request = this.buildCreateRequest(title, action);
    const call = this.ownerCreateCall(request);
    if (!call) {
      this.appendStep(
        createLocalStep(
          nextLocalId(++this.localIdCounter),
          this.steps().length,
          action,
          request.expectedResult ?? null,
          request.notes ?? null,
          { title, tcdl: request.tcdl ?? null },
        ),
      );
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: (created) => {
        this.appendStep(created);
        this.busy.set(false);
      },
      error: () => this.failMutation(),
    });
  }

  private appendStep(step: TestStep): void {
    this.steps.update((list) => [...list, step]);
    this.draftStep.set(null);
    this.resetEditState();
    this.emitChange();
  }

  private buildCreateRequest(title: string, action: string): CreateTestStepRequest {
    return {
      kind: 'ACTION',
      title,
      action,
      expectedResult: this.editExpected().trim() || null,
      notes: this.editNotes().trim() || null,
      tcdl: this.editTcdl().trim() ? this.editTcdl() : null,
    };
  }

  private resetEditState(): void {
    this.editingId.set(null);
    this.editTitle.set('');
    this.editAction.set('');
    this.editExpected.set('');
    this.editNotes.set('');
    this.editTcdl.set('');
    this.editTcdlOriginal = '';
  }

  private removeStep(step: TestStep): void {
    if (this.busy()) {
      return;
    }
    if (isLocalId(step.id) || this.isBuffered()) {
      this.steps.update((list) => list.filter((s) => s.id !== step.id));
      this.emitChange();
      return;
    }
    const call = this.ownerDeleteCall(step.id);
    if (!call) {
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: () => {
        this.steps.update((list) => list.filter((s) => s.id !== step.id));
        this.busy.set(false);
        this.emitChange();
      },
      error: () => this.failMutation(),
    });
  }

  private swap(from: number, to: number): void {
    if (this.busy()) {
      return;
    }
    const previous = this.steps();
    const reordered = [...previous];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    if (this.isBuffered()) {
      this.steps.set(reordered);
      this.emitChange();
      return;
    }
    this.persistOrder(reordered, previous);
  }

  private persistOrder(reordered: TestStep[], previous: TestStep[]): void {
    const call = this.ownerReorderCall(reordered.map((s) => s.id));
    if (!call) {
      return;
    }
    this.steps.set(reordered);
    this.emitChange();
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: (result) => {
        this.steps.set(result);
        this.busy.set(false);
      },
      error: () => {
        this.steps.set(previous);
        this.failMutation();
      },
    });
  }

  private updateLocalStep(id: string, title: string, action: string): void {
    const expectedResult = this.editExpected().trim() || null;
    const notes = this.editNotes().trim() || null;
    // NEX-615: Titel und Aktion sind getrennte Felder; die Aktion traegt nicht mehr
    // faelschlich den Titel.
    // NEX-546: Der fruehere NEX-548-Detach (Kappen von libraryStepId beim Edit) ist
    // zurueckgebaut. Die Definition einer verknuepften Zeile ist geteilt — Editieren behaelt
    // die Referenz, damit die Aenderung ueberall wirkt.
    const tcdl = this.editTcdl().trim() ? this.editTcdl() : null;
    this.steps.update((list) =>
      list.map((s) => (s.id === id ? { ...s, title, action, expectedResult, notes, tcdl } : s)),
    );
    this.cancelEdit();
    this.emitChange();
  }

  flushPendingSteps(newTestCaseId: string): Observable<FlushResult> {
    return this.flushPendingToTarget(
      (req) => this.stepService.create(this.projectId(), newTestCaseId, req),
      (libId) => this.stepService.copyFromLibrary(this.projectId(), newTestCaseId, libId),
    );
  }

  /** Modul-Variante (NEX-593): persistiert lokal gesammelte Schritte nach dem Modul-Create. */
  flushPendingStepsForModule(newModuleId: string): Observable<FlushResult> {
    return this.flushPendingToTarget(
      (req) => this.stepService.createForModule(this.projectId(), newModuleId, req),
      (libId) => this.stepService.copyFromLibraryToModule(this.projectId(), newModuleId, libId),
    );
  }

  private flushPendingToTarget(
    create: (req: CreateTestStepRequest) => Observable<TestStep>,
    copyLibrary: (libraryStepId: string) => Observable<TestStep>,
  ): Observable<FlushResult> {
    const pending = this.steps().filter((s) => isLocalId(s.id));
    if (pending.length === 0) {
      return of({ persisted: [], failed: [] });
    }
    return from(pending).pipe(
      concatMap((s) => {
        // NEX-546: Verknuepfte Zeilen (libraryStepId gesetzt) werden ueber copyFromLibrary
        // persistiert — auch wenn inline editiert. Das BE verknuepft die geteilte Definition und
        // wendet die mitgelieferten Inhalte darauf an (kein Detach mehr, ex-NEX-548). Nur echte,
        // neu getippte Schritte laufen ueber create().
        const call$ = s.libraryStepId
          ? copyLibrary(s.libraryStepId)
          : create({
              kind: 'ACTION',
              title: s.title ?? null,
              action: s.action ?? '',
              expectedResult: s.expectedResult ?? null,
              notes: s.notes ?? null,
              tcdl: s.tcdl ?? null,
            });
        return call$.pipe(catchError(() => of({ failed: s })));
      }),
      toArray(),
      concatMap((items) =>
        of({
          persisted: items.filter(isPersisted),
          failed: items.filter(isFailed).map((f) => f.failed),
        }),
      ),
    );
  }

  private failMutation(): void {
    this.error.set(true);
    this.busy.set(false);
  }

  /** Combined dirty signal (NEX-535) + steps snapshot (NEX-599) for the parent form. */
  private emitChange(): void {
    this.changed.emit();
    this.stepsChanged.emit(this.steps());
  }

  private ownerListCall(): Observable<TestStep[]> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.list(this.projectId(), tcId);
    const mId = this.moduleId();
    if (mId) return this.stepService.listForModule(this.projectId(), mId);
    return null;
  }

  private ownerCreateCall(request: CreateTestStepRequest): Observable<TestStep> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.create(this.projectId(), tcId, request);
    const mId = this.moduleId();
    if (mId) return this.stepService.createForModule(this.projectId(), mId, request);
    return null;
  }

  private ownerUpdateCall(stepId: string, request: UpdateTestStepRequest): Observable<TestStep> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.update(this.projectId(), tcId, stepId, request);
    const mId = this.moduleId();
    if (mId) return this.stepService.updateForModule(this.projectId(), mId, stepId, request);
    return null;
  }

  private ownerDeleteCall(stepId: string): Observable<void> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.delete(this.projectId(), tcId, stepId);
    const mId = this.moduleId();
    if (mId) return this.stepService.deleteForModule(this.projectId(), mId, stepId);
    return null;
  }

  private ownerReorderCall(orderedIds: string[]): Observable<TestStep[]> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.reorder(this.projectId(), tcId, orderedIds);
    const mId = this.moduleId();
    if (mId) return this.stepService.reorderForModule(this.projectId(), mId, orderedIds);
    return null;
  }

  private ownerCopyFromLibraryCall(libraryStepId: string): Observable<TestStep> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.copyFromLibrary(this.projectId(), tcId, libraryStepId);
    const mId = this.moduleId();
    if (mId) return this.stepService.copyFromLibraryToModule(this.projectId(), mId, libraryStepId);
    return null;
  }

  private inputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }
}
